import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertCircle, CheckCircle, Link, Search, Trash2, Camera, ExternalLink, Upload } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyCamService } from '@/services/companyCamService';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

const CompanyCamTab = ({ project }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCompanyCamProject, setSelectedCompanyCamProject] = useState(null);
  const [linkMode, setLinkMode] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkOption, setLinkOption] = useState('create'); // 'create' or 'link'
  const [projectsPage, setProjectsPage] = useState(1);
  const [allProjects, setAllProjects] = useState([]);
  const [hasMoreProjects, setHasMoreProjects] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const fileInputRef = React.useRef(null);

  const projectId = project?.id || project?._id;

  // Debounce search term with 500ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 and clear results when search term changes
      if (searchTerm !== debouncedSearchTerm) {
        setProjectsPage(1);
        setAllProjects([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch CompanyCam configuration status
  const { data: configData, isLoading: loadingConfig } = useQuery({
    queryKey: ['companycam-config'],
    queryFn: () => companyCamService.getConfigStatus()
  });

  // Fetch mapping data
  const { data: mappingData, isLoading: loadingMapping, refetch: refetchMapping } = useQuery({
    queryKey: ['companycam-project-mapping', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      try {
        const result = await companyCamService.getProjectMapping(projectId);
        return result;
      } catch (error) {
        console.error('Error fetching project mapping:', error);
        toast({
          title: "Error Fetching Link Status",
          description: error?.detail || "Could not load CompanyCam link status.",
          variant: "destructive",
        });
        return null;
      }
    },
    enabled: !!projectId
  });

  // Fetch CompanyCam projects with search or all projects based on debounced term
  const { data: projectsData, isLoading: loadingProjects, refetch: refetchProjects } = useQuery({
    queryKey: ['companycam-projects', debouncedSearchTerm, projectsPage],
    queryFn: () => {
      // If search term exists, use search API
      if (debouncedSearchTerm) {
        return companyCamService.searchProjects(debouncedSearchTerm, projectsPage, 25);
      }
      // Otherwise, fetch all projects
      return companyCamService.getAllProjects(projectsPage, 25);
    },
    enabled: linkMode && linkOption === 'link',
    staleTime: 5 * 60 * 1000
  });

  // Update allProjects when data changes
  useEffect(() => {
    if (projectsData?.data) {
      if (projectsPage === 1) {
        // First page - replace results
        setAllProjects(projectsData.data.projects || []);
      } else {
        // Subsequent pages - append results
        setAllProjects(prev => [...prev, ...(projectsData.data.projects || [])]);
      }
      setHasMoreProjects(projectsData.data.has_more || false);
    }
  }, [projectsData, projectsPage]);

  // Mutations
  const createProjectMutation = useMutation({
    mutationFn: () => companyCamService.createProject({
      crm_project_id: projectId,
      name: project.name,
      address: project.jobsite_address || project.address,
      customer_name: project.customer?.name || project.customer
    }),
    onSuccess: () => {
      toast({ description: "CompanyCam project created and linked successfully!" });
      refetchMapping();
      setLinkDialogOpen(false);
      setLinkOption('create');
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create CompanyCam project"
      });
    }
  });

  const linkProjectMutation = useMutation({
    mutationFn: (companyCamProjectId) => companyCamService.linkProject({
      crm_project_id: projectId,
      companycam_project_id: companyCamProjectId
    }),
    onSuccess: () => {
      toast({ description: "CompanyCam project linked successfully!" });
      refetchMapping();
      setLinkDialogOpen(false);
      setSelectedCompanyCamProject(null);
      setLinkMode(false);
      setSearchTerm('');
      setLinkOption('create');
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to link CompanyCam project"
      });
    }
  });

  const unlinkProjectMutation = useMutation({
    mutationFn: () => companyCamService.unlinkProject(projectId),
    onSuccess: () => {
      toast({ description: "CompanyCam project unlinked successfully!" });
      refetchMapping();
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to unlink CompanyCam project"
      });
    }
  });

  const handleOAuthSetup = () => {
    if (configData?.data?.oauth_url) {
      const w = window.open(configData.data.oauth_url, '_blank');
      if (!w) {
        alert('Please allow popups for this site');
      }
    }
  };

  const handleLinkProject = () => {
    setLinkDialogOpen(true);
  };

  const handleConfirmLink = () => {
    if (linkOption === 'create') {
      createProjectMutation.mutate();
    } else if (linkOption === 'link' && selectedCompanyCamProject) {
      linkProjectMutation.mutate(selectedCompanyCamProject.id);
    } else {
      toast({
        variant: "destructive",
        description: "Please select a CompanyCam project to link"
      });
    }
  };

  const handleUnlinkProject = () => {
    if (!mappingData?.data?.linked) {
      return;
    }
    unlinkProjectMutation.mutate();
  };

  const companyCamProjectId = mappingData?.data?.companycam_project?.id;

  const handlePhotoFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!companyCamProjectId) {
      toast({ variant: "destructive", description: "No linked CompanyCam project found." });
      return;
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let success = 0;
    const failures = [];

    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      try {
        await companyCamService.uploadPhoto(companyCamProjectId, fd);
        success++;
      } catch (err) {
        failures.push(`${files[i].name}: ${err?.detail || err?.message || "failed"}`);
      }
      setUploadProgress({ done: i + 1, total: files.length });
    }

    setUploading(false);
    // Reset the input so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (success > 0) {
      toast({ description: `${success} photo${success !== 1 ? 's' : ''} uploaded to CompanyCam${failures.length ? `, ${failures.length} failed` : ''}` });
    }
    if (failures.length > 0) {
      toast({ variant: "destructive", description: failures.slice(0, 3).join("; ") + (failures.length > 3 ? ` (+${failures.length - 3} more)` : "") });
    }
  };

  const isLoading = loadingMapping || createProjectMutation.isPending || unlinkProjectMutation.isPending || linkProjectMutation.isPending;

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-pulse" />
          <p className="text-gray-500">Loading CompanyCam status...</p>
        </div>
      </div>
    );
  }

  if (!configData?.data?.configured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-yellow-600">
            <Camera className="mr-2 h-5 w-5" />
            CompanyCam Setup Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Camera className="h-16 w-16 text-yellow-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              CompanyCam Integration Not Configured
            </h3>
            <div className="text-gray-600 mb-6 max-w-lg mx-auto space-y-2">
              <p>To connect to CompanyCam, you need to add your OAuth credentials to the backend configuration.</p>
              <div className="text-left bg-gray-50 rounded-lg p-4 mt-4">
                <p className="font-medium text-sm mb-2">Required Configuration:</p>
                <ul className="text-sm space-y-1 text-gray-700">
                  <li>• <code>COMPANYCAM_CLIENT_ID</code></li>
                  <li>• <code>COMPANYCAM_CLIENT_SECRET</code></li>
                  <li>• <code>COMPANYCAM_SECURITY_PHRASE</code></li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!configData?.data?.authenticated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-blue-600">
            <Camera className="mr-2 h-5 w-5" />
            CompanyCam Authentication Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Camera className="h-16 w-16 text-blue-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Connect to CompanyCam
            </h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              CompanyCam is configured but needs authentication. Click below to connect your CompanyCam account.
            </p>
            
            <Button 
              onClick={handleOAuthSetup}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Authenticate with CompanyCam
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {isLoading && <LoadingSpinner />}
      
      {mappingData?.data?.linked ? (
        // Already linked to CompanyCam
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Camera className="mr-2 h-5 w-5 text-blue-600" />
                CompanyCam Project
              </div>
              <Badge className="bg-blue-100 text-blue-800">Linked</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-blue-200 bg-blue-50">
              <CheckCircle className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-900">Connected</AlertTitle>
              <AlertDescription className="text-blue-800">
                This project is linked to CompanyCam project
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Project Name</p>
                <p className="text-sm text-gray-900">{mappingData.data.companycam_project.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">CompanyCam ID</p>
                <p className="text-sm text-gray-900 font-mono">{mappingData.data.companycam_project.id}</p>
              </div>
              {mappingData.data.companycam_project.address && (
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-700">Address</p>
                  <p className="text-sm text-gray-900">{mappingData.data.companycam_project.address}</p>
                </div>
              )}
              {mappingData.data.companycam_project.customer_name && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Customer</p>
                  <p className="text-sm text-gray-900">{mappingData.data.companycam_project.customer_name}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-700">Linked At</p>
                <p className="text-sm text-gray-900">
                  {mappingData.data.mapping?.linked_at ? 
                    new Date(mappingData.data.mapping.linked_at).toLocaleDateString() : 
                    'Recently'
                  }
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              {/* Hidden file input for photo upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoFilesSelected}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? `Uploading ${uploadProgress.done}/${uploadProgress.total}...` : 'Add Photos'}
              </Button>
              <Button
                onClick={() => { const w = window.open(mappingData.data.companycam_project.companycam_url, '_blank'); if (!w) alert('Please allow popups for this site'); }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                View in CompanyCam
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleUnlinkProject}
                disabled={unlinkProjectMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {unlinkProjectMutation.isPending ? 'Unlinking...' : 'Unlink Project'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Not linked - show options to create or link
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Camera className="mr-2 h-5 w-5 text-gray-400" />
              Link to CompanyCam
            </CardTitle>
            <CardDescription>
              Connect this project to CompanyCam for photo documentation and project tracking
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-gray-200 bg-gray-50">
              <AlertCircle className="h-4 w-4 text-gray-600" />
              <AlertTitle className="text-gray-900">Not Linked</AlertTitle>
              <AlertDescription className="text-gray-700">
                This project is not yet connected to a CompanyCam project
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button 
                onClick={handleLinkProject}
                disabled={createProjectMutation.isPending || linkProjectMutation.isPending}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {createProjectMutation.isPending || linkProjectMutation.isPending ? 'Linking...' : 'Link to CompanyCam'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Link to CompanyCam</DialogTitle>
            <DialogDescription>
              Choose how you want to connect this project to CompanyCam
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <RadioGroup value={linkOption} onValueChange={(value) => {
              setLinkOption(value);
              if (value === 'link') {
                setLinkMode(true); // Enable query when "link" is selected
              }
            }}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="create" id="create" />
                <Label htmlFor="create" className="cursor-pointer">
                  Create a new CompanyCam project and link it
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="link" id="link" />
                <Label htmlFor="link" className="cursor-pointer">
                  Link to an existing CompanyCam project
                </Label>
              </div>
            </RadioGroup>

            {linkOption === 'create' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900 mb-2 font-medium">Creating new project with:</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Name: {project?.name}</li>
                  <li>• Address: {project?.jobsite_address || project?.address || 'Not specified'}</li>
                  <li>• Customer: {project?.customer?.name || project?.customer || 'Not specified'}</li>
                </ul>
              </div>
            )}

            {linkOption === 'link' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search all CompanyCam projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {loadingProjects && projectsPage === 1 && (
                  <div className="text-center py-4">
                    <LoadingSpinner />
                    <p className="text-sm text-gray-500 mt-2">
                      {debouncedSearchTerm ? 'Searching...' : 'Loading projects...'}
                    </p>
                  </div>
                )}

                {!loadingProjects && allProjects.length === 0 && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    {debouncedSearchTerm 
                      ? `No projects found matching "${debouncedSearchTerm}"`
                      : 'No CompanyCam projects found. Create projects in CompanyCam first.'}
                  </div>
                )}

                {allProjects.length > 0 && (
                  <div className="space-y-2">
                    {hasMoreProjects && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setProjectsPage(prev => prev + 1)}
                        disabled={loadingProjects}
                        className="w-full"
                      >
                        {loadingProjects && projectsPage > 1 ? 'Loading...' : `Load More (${allProjects.length} loaded)`}
                      </Button>
                    )}
                    
                    <div className="border rounded-lg max-h-80 overflow-y-auto">
                      {allProjects.map((proj) => (
                        <div
                          key={proj.id}
                          onClick={() => {
                            setSelectedCompanyCamProject(proj);
                          }}
                          className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                            selectedCompanyCamProject?.id === proj.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                          }`}
                        >
                          <div className="font-medium text-sm">{proj.name}</div>
                          {proj.address && (
                            <div className="text-xs text-gray-600 mt-1">{proj.address}</div>
                          )}
                          {proj.customer_name && (
                            <div className="text-xs text-gray-500 mt-1">Customer: {proj.customer_name}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">ID: {proj.id}</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="text-xs text-gray-500 text-center">
                      Showing {allProjects.length} project{allProjects.length !== 1 ? 's' : ''}
                      {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
                      {hasMoreProjects && ' (more available)'}
                    </div>
                  </div>
                )}

                {selectedCompanyCamProject && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <AlertTitle className="text-blue-900">Selected</AlertTitle>
                    <AlertDescription className="text-blue-800">
                      {selectedCompanyCamProject.name}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setLinkDialogOpen(false);
                  setSelectedCompanyCamProject(null);
                  setLinkMode(false);
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                  setLinkOption('create');
                  setProjectsPage(1);
                  setAllProjects([]);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmLink}
                disabled={
                  (linkOption === 'link' && !selectedCompanyCamProject) ||
                  createProjectMutation.isPending ||
                  linkProjectMutation.isPending
                }
                className="bg-blue-600 hover:bg-blue-700"
              >
                {createProjectMutation.isPending || linkProjectMutation.isPending
                  ? 'Processing...'
                  : linkOption === 'create'
                  ? 'Create & Link'
                  : 'Link Project'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyCamTab;
